import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useSnackbar } from 'notistack';
import { configureWorkyAmplify } from './WorkyAmplifyConfig';
import { confirmNewPasswordSignInChallenge, signOutCognitoSession } from './WorkyCognitoAuthService';
import { handleCognitoPasswordSignInOutcome } from './WorkyCustomAuthSignInOutcomeService';
import {
    clearPendingNewPasswordChallengeState,
    readPendingNewPasswordChallengeState,
} from './WorkyPendingNewPasswordChallengeService';
import type { PendingNewPasswordChallengeState } from './WorkyPendingNewPasswordChallengeInterfaces';
import { WORKY_WEB_APP_PATHS } from '../WorkyWebAppPaths';
import { WORKY_BRAND_LOGO_SRC } from '../common/WorkyBrandConstants';
import { isValidString } from '../common/CommonUtilsMethods';
import {
    WORKY_SUPPORT_EMAIL,
    WORKY_SUPPORT_PHONE_DIGITS,
    WORKY_SUPPORT_PHONE_DISPLAY,
} from '../common/WorkySupportContactConstants';
import {
    isValidWorkyPassword,
} from './WorkyPasswordValidationService';
import { WorkyPasswordCriteriaChecklist } from './WorkyPasswordCriteriaChecklist';
import './WorkyLoginPage.css';

const WorkyNewPasswordChallengePage = (): JSX.Element => {
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [pendingNewPasswordChallengeState, setPendingNewPasswordChallengeState] =
        useState<PendingNewPasswordChallengeState | null>(null);
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
    const [isPasswordCriteriaVisible, setIsPasswordCriteriaVisible] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        configureWorkyAmplify();
        const storedPendingNewPasswordChallengeState: PendingNewPasswordChallengeState | null =
            readPendingNewPasswordChallengeState();
        if (storedPendingNewPasswordChallengeState === null) {
            history.replace(WORKY_WEB_APP_PATHS.login);
            return;
        }
        setPendingNewPasswordChallengeState(storedPendingNewPasswordChallengeState);
    }, [history]);

    const handleSubmit = useCallback(
        async (submitEvent: React.FormEvent<HTMLFormElement>): Promise<void> => {
            submitEvent.preventDefault();

            if (isSubmitting || pendingNewPasswordChallengeState === null) {
                return;
            }

            if (!isValidWorkyPassword(newPassword)) {
                enqueueSnackbar('Please meet all password requirements before continuing.', {
                    variant: 'warning',
                });
                return;
            }

            if (newPassword !== confirmNewPassword) {
                enqueueSnackbar('Passwords do not match.', { variant: 'warning' });
                return;
            }

            setIsSubmitting(true);
            try {
                const signInOutcome = await confirmNewPasswordSignInChallenge({
                    emailAddress: pendingNewPasswordChallengeState.emailAddress,
                    newPassword,
                });

                await handleCognitoPasswordSignInOutcome({
                    signInOutcome,
                    history,
                    enqueueSnackbar,
                    primaryWorkyCorpId: pendingNewPasswordChallengeState.primaryWorkyCorpId,
                    successMessage: 'Password updated. Signed in successfully.',
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [
            confirmNewPassword,
            enqueueSnackbar,
            history,
            isSubmitting,
            newPassword,
            pendingNewPasswordChallengeState,
        ]
    );

    const handleBackToLoginClick = useCallback((): void => {
        clearPendingNewPasswordChallengeState();
        void signOutCognitoSession();
        history.push(WORKY_WEB_APP_PATHS.login);
    }, [history]);

    const handleNavigateHomeClick = useCallback((): void => {
        history.push(WORKY_WEB_APP_PATHS.home);
    }, [history]);

    if (pendingNewPasswordChallengeState === null) {
        return (
            <div className="worky-login-page">
                <div className="worky-login-card worky-login-card-loading">
                    <CircularProgress size={32} />
                    <p className="worky-login-subtitle">Loading…</p>
                </div>
            </div>
        );
    }

    const emailAddressDisplay: string = pendingNewPasswordChallengeState.emailAddress;

    return (
        <div className="worky-login-page">
            <button
                type="button"
                className="worky-login-back-home-button"
                onClick={handleBackToLoginClick}
            >
                <ArrowBackIcon fontSize="small" />
                Back to sign in
            </button>
            <div className="worky-login-card">
                <button
                    type="button"
                    className="worky-login-logo-button"
                    onClick={handleNavigateHomeClick}
                    aria-label="Worky home"
                >
                    <img src={WORKY_BRAND_LOGO_SRC} alt="Worky" className="worky-login-logo-image" />
                </button>

                <h1 className="worky-login-title">Set a new password</h1>
                <p className="worky-login-subtitle">
                    Your account for <strong>{emailAddressDisplay}</strong> requires a new password before
                    you can sign in.
                </p>

                <form className="worky-login-form" onSubmit={handleSubmit}>
                    <TextField
                        label="New password"
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(changeEvent): void => setNewPassword(changeEvent.target.value)}
                        onFocus={(): void => setIsPasswordCriteriaVisible(true)}
                        onBlur={(): void => {
                            if (!isValidString(newPassword)) {
                                setIsPasswordCriteriaVisible(false);
                            }
                        }}
                        required
                        fullWidth
                    />
                    <WorkyPasswordCriteriaChecklist
                        password={newPassword}
                        isVisible={isPasswordCriteriaVisible}
                    />
                    <TextField
                        label="Confirm new password"
                        type="password"
                        autoComplete="new-password"
                        value={confirmNewPassword}
                        onChange={(changeEvent): void => setConfirmNewPassword(changeEvent.target.value)}
                        required
                        fullWidth
                    />
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="worky-login-submit-button"
                        disabled={isSubmitting}
                        fullWidth
                    >
                        {isSubmitting ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'Set password and sign in'
                        )}
                    </Button>
                </form>

                <div className="worky-login-support-footer">
                    <span className="worky-login-support-label">Need help? Contact Us</span>{' '}
                    <a className="worky-login-support-link" href={`mailto:${WORKY_SUPPORT_EMAIL}`}>
                        {WORKY_SUPPORT_EMAIL}
                    </a>
                    {' · '}
                    <a className="worky-login-support-link" href={`tel:${WORKY_SUPPORT_PHONE_DIGITS}`}>
                        {WORKY_SUPPORT_PHONE_DISPLAY}
                    </a>
                </div>
            </div>
        </div>
    );
};

export { WorkyNewPasswordChallengePage };
